import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  MyIcon,
  Question,
  SelectLang,
} from '@/components';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { listToTree, normalizePath } from './lib';
import ForbiddenPage from './pages/403';
import {
  // getMenus,
  getUserMenus,
  type MenuItem,
  queryCurrentUser,
} from './services';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/login';

const fetchUserInfo = async () => {
  try {
    const res = await queryCurrentUser();
    return res.data;
  } catch (_error) {
    history.push(loginPath);
  }
  return undefined;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  menu?: any[];
  authMenu?: string[];
  allMenus?: string[];
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, `${loginPath}/success`, `${loginPath}/fail`].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();

    let menu: Partial<MenuItem>[] = [];
    let allMenus: string[] = [];
    let authMenu: string[] = [];
    if (currentUser?.userId) {
      // const menuData = await getMenus();  //全部菜单
      const menuData = await getUserMenus(currentUser?.userId); //当前用户的菜单
      menu =
        menuData?.data.list?.filter((item: any) => item.status === 'enabled') ||
        [];
      allMenus = menuData?.data?.allMenus || [];
      authMenu =
        (menu
          ?.filter((item) => item.component)
          .map((item) => item.component) as string[]) || [];
    }

    menu = menu.map((item: any) => ({
      name: item.name,
      path: item.path,
      icon: <MyIcon icon={item.icon} />,
      component: item.component,
      layout: item.layout,
      _id: item._id,
      parentId: item.parentId,
    }));

    return {
      fetchUserInfo,
      currentUser,
      menu: listToTree(menu, {
        id: '_id',
        children: 'routes',
      }), // 当前用户菜单 树形展示
      authMenu: authMenu, // 当前用户菜单列表
      allMenus: allMenus, //系统全部的菜单
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    menu: [],
    authMenu: [],
    allMenus: [],
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

/**
 *  动态注入 routes：把接口返回的菜单转成 routes，挂到 '/' 布局下面
 *  菜单合并
 */
// export function patchRoutes({ routes }: any) {
//   const root = routes.find((r: any) => r.path === '/');
//   if (!root) return;

//   const staticRoutes = root.routes || [];

//   root.routes = [
//     ...(dynamicRoutes || []),
//     ...staticRoutes.filter((r: any) => r.path === '/welcome' || r.path === '*'),
//   ];
// }

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src:
        initialState?.currentUser?.avatar ||
        'https://gw.alipayobjects.com/zos/rmsportal/BiazfanxmamNRoxxVxka.png',
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
        return;
      }

      const path = normalizePath(history.location.pathname);
      // 404
      if (!initialState?.allMenus?.includes(path)) {
        history.replace('/404');
        return;
      }

      // 403 没有权限访问
      if (!initialState?.authMenu?.includes(path)) {
        history.replace('/403');
        return;
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    unAccessible: <ForbiddenPage />,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    // 用接口菜单覆盖 ProLayout 菜单   左侧菜单出来了 但是访问404
    menu: {
      // 每当 initialState?.currentUser?.userid 发生修改时重新执行 request
      params: {
        userId: initialState?.currentUser?.userid,
      },
      request: async (_, defaultMenuData) => {
        return initialState?.menu?.length
          ? initialState?.menu
          : defaultMenuData;
      },
    },
    ...initialState?.settings,
  };
};

// let extraRoutes: any[] = [];
// export async function patchRoutes({ routes }) {
//   let res = await getInitialState()
//   console.log('routes', routes,res );

//   routes[1].routes = routes[1].routes.concat(extraRoutes);
//   // 如果最后页面是404，所以要插入到时候第二个
//   // routes[1].routes.splice(-1, 0, ...extraRoutes);
//   console.log(history.location.pathname, routes)
//   console.log(extraRoutes)
// }
// export function render(oldRender) {
//   // fetch('/api/routes').then(res=>res.json()).then((res) => {
//   extraRoutes = [{
//     path: '/welcome2',
//     name: 'welcome',
//     icon: 'smile',
//     // hideInMenu: true,
//     exact: true,
//     access: "canAdmin",
//     // component: dynamic({ loader: () => import('./pages/Form'), loading: LoadingComponent }),
//   }];
//   oldRender();
//   // })
// }

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: 'https://proapi.azurewebsites.net',
  ...errorConfig,
};
